import React, { useState, useEffect } from 'react';
import { Grid, Typography, Button, Snackbar, FormControl, FormControlLabel, RadioGroup, Radio, InputLabel, Select, MenuItem, Box } from '@mui/material';
import { Save as SaveIcon, Close as CloseIcon } from '@mui/icons-material';
import FormField from './FormField';
import OpcoesResposta from './OpcoesResposta';
import PerguntaService from 'services/perguntasService';

const PerguntaForm = ({ pergunta, handleSubmit, closeModal, atualizarPerguntas, isSubPergunta, perguntaPai }) => {
  const [codigo, setCodigo] = useState('');
  const [textoPergunta, setTextoPergunta] = useState('');
  const [tipoResposta, setTipoResposta] = useState('radio');
  const [opcoesResposta, setOpcoesResposta] = useState([]);
  const [obrigatoria, setObrigatoria] = useState(false);
  const [status, setStatus] = useState('ativo');
  const [subPergunta, setSubPergunta] = useState(isSubPergunta || false);
  const [paiId, setPaiId] = useState(perguntaPai ? perguntaPai.id : null);
  const [perguntasPai, setPerguntasPai] = useState([]);
  const [secao, setSecao] = useState(1);
  const [cadastroSucesso, setCadastroSucesso] = useState(false);

  useEffect(() => {
    if (pergunta) {
      setCodigo(pergunta.codigo || '');
      setTextoPergunta(pergunta.texto || '');
      setTipoResposta(pergunta.tipo_resposta || 'radio');
      setOpcoesResposta(pergunta.opcoes_resposta || []);
      setObrigatoria(pergunta.obrigatoria || false);
      setStatus(pergunta.status || 'ativo');
      setPaiId(pergunta.pai_id || null);
      setSubPergunta(Boolean(pergunta.pai_id));
      setSecao(pergunta.secao || 1);
    }

    async function fetchPerguntasPai() {
      try {
        const perguntas = await PerguntaService.getPerguntas();
        setPerguntasPai(perguntas.filter(p => !p.pai_id));
      } catch (error) {
        console.error('Erro ao carregar perguntas pai:', error);
      }
    }

    fetchPerguntasPai();
  }, [pergunta]);

  const handleAddOpcao = () => {
    setOpcoesResposta([...opcoesResposta, '']);
  };

  const handleOpcaoChange = (indice, valor) => {
    const novasOpcoes = [...opcoesResposta];
    novasOpcoes[indice] = valor;
    setOpcoesResposta(novasOpcoes);
  };

  const onSubmit = async () => {
    const data = {
      codigo,
      texto: textoPergunta,
      tipo_resposta: tipoResposta,
      opcoes_resposta: (tipoResposta === 'radio' || tipoResposta === 'checkbox' || tipoResposta === 'select') ? opcoesResposta : [],
      obrigatoria,
      status,
      secao,
      pai_id: subPergunta ? paiId : null,
    };

    await handleSubmit(data);
    setCadastroSucesso(true); // Exibe o Snackbar

    // Mova o fechamento do modal para após um atraso ou manualmente
    setTimeout(() => {
      closeModal();
      atualizarPerguntas();
    }, 1000); // Fecha o modal após 1 segundo
  };

  // Teste o Snackbar manualmente (apenas para debug)
  const testSnackbar = () => {
    setCadastroSucesso(true);
  };

  return (
    <Grid container direction="column" alignItems="center" sx={{ minHeight: '10vh', p: 3, backgroundColor: 'white', boxShadow: 3, borderRadius: 2, fontSize: '14px' }}>
      <Typography variant="h1" gutterBottom sx={{ fontSize: '20px' }}>{pergunta ? 'Editar Pergunta' : 'Nova Pergunta'}</Typography>

      <FormField label="Código da Pergunta" value={codigo} onChange={e => setCodigo(e.target.value)} />
      <FormField label="Digite sua pergunta" value={textoPergunta} onChange={e => setTextoPergunta(e.target.value)} />

      {/* Colocar os Radios lado a lado */}
      <Box sx={{ display: 'flex', flexDirection: 'row', mb: 2 }}>
        <FormControl component="fieldset" sx={{ mr: 3 }}>
          <RadioGroup
            aria-label="subpergunta"
            name="subpergunta"
            value={subPergunta.toString()}
            onChange={e => setSubPergunta(e.target.value === 'true')}
            row // Alinhar os botões de rádio horizontalmente
          >
            <FormControlLabel value="false" control={<Radio />} label="Pergunta Principal" />
            <FormControlLabel value="true" control={<Radio />} label="Subpergunta" />
          </RadioGroup>
        </FormControl>
      </Box>

      {subPergunta && (
        <FormControl variant="outlined" fullWidth sx={{ mb: 2 }}>
          <InputLabel>Pergunta Pai</InputLabel>
          <Select
            value={paiId || ''}
            onChange={e => setPaiId(e.target.value)}
            label="Pergunta Pai"
          >
            <MenuItem value="">
              <em>Nenhuma</em>
            </MenuItem>
            {perguntasPai.map(pergunta => (
              <MenuItem key={pergunta.id} value={pergunta.id}>
                {pergunta.codigo} - {pergunta.texto}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <FormField
        label="Tipo de Resposta"
        value={tipoResposta}
        onChange={e => setTipoResposta(e.target.value)}
        type="select"
        options={[
          { label: 'Radio', value: 'radio' },
          { label: 'Select', value: 'select' },
          { label: 'Placeholder', value: 'placeholder' },
          { label: 'Checkbox', value: 'checkbox' },
        ]}
      />

      {(tipoResposta === 'radio' || tipoResposta === 'checkbox' || tipoResposta === 'select') && (
        <OpcoesResposta opcoesResposta={opcoesResposta} handleAddOpcao={handleAddOpcao} handleOpcaoChange={handleOpcaoChange} />
      )}

      {/* Colocando os selects lado a lado */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <FormField
            label="É obrigatória?"
            value={obrigatoria.toString()}
            onChange={e => setObrigatoria(e.target.value === 'true')}
            type="select"
            options={[
              { label: 'Sim', value: true },
              { label: 'Não', value: false },
            ]}
          />
        </Grid>

        <Grid item xs={6}>
          <FormField
            label="Status"
            value={status}
            onChange={e => setStatus(e.target.value)}
            type="select"
            options={[
              { label: 'Ativo', value: 'ativo' },
              { label: 'Inativo', value: 'inativo' },
            ]}
          />
        </Grid>
      </Grid>

      <FormField
        label="Seção"
        value={secao}
        onChange={e => setSecao(parseInt(e.target.value))}
        type="select"
        options={[
          { label: 'Mapa Geral', value: 1 },
          { label: 'Mapa Fronteira', value: 2 },
          { label: 'Mapa Assistência', value: 3 },
        ]}
      />

      <Grid container justifyContent="space-between" sx={{ mt: 3, width: '100%' }}>
        <Button startIcon={<CloseIcon />} variant="outlined" color="error" onClick={closeModal}>
          Cancelar
        </Button>
        <Button startIcon={<SaveIcon />} variant="contained" color="primary" onClick={onSubmit}>
          {pergunta ? 'Atualizar' : 'Enviar'}
        </Button>
      </Grid>

      <Snackbar
        open={cadastroSucesso}
        autoHideDuration={4000}
        onClose={() => setCadastroSucesso(false)}
        message={pergunta ? "Pergunta atualizada com sucesso" : "Pergunta cadastrada com sucesso"}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Grid>
  );
};

export default PerguntaForm;
